import React from 'react';
import { motion } from 'framer-motion';
import { FaFutbol, FaPalette, FaTrophy, FaUsers, FaMusic, FaTheaterMasks } from 'react-icons/fa';
import SEO from '../components/SEO';

const StudentLifePage = () => {
  const activities = [
    {
      icon: FaFutbol,
      title: 'Sports & Athletics',
      description: 'Basketball, Football, Cricket, Athletics, Table Tennis',
      color: 'bg-orange-500'
    },
    {
      icon: FaPalette,
      title: 'Arts & Crafts',
      description: 'Painting, Drawing, Sculpture, Pottery making',
      color: 'bg-pink-500'
    },
    {
      icon: FaMusic,
      title: 'Music & Dance',
      description: 'Classical, Folk, Western dance forms and instruments',
      color: 'bg-purple-500'
    },
    {
      icon: FaTheaterMasks,
      title: 'Drama & Theater',
      description: 'Stage performances, Role plays, Skits',
      color: 'bg-blue-500'
    },
    {
      icon: FaUsers,
      title: 'Clubs & Societies',
      description: 'Science Club, Literary Club, Eco Club, NCC',
      color: 'bg-green-500'
    },
    {
      icon: FaTrophy,
      title: 'Competitions',
      description: 'Inter-school events, Olympiads, Quizzes',
      color: 'bg-yellow-500'
    }
  ];

  return (
    <>
      <SEO 
        title="Student Life & Activities | Vidya Vatika World School Panipat"
        description="Experience vibrant student life with clubs, sports, arts, leadership opportunities and more at Vidya Vatika World School."
        keywords="student life Panipat, school activities, extracurricular Haryana, holistic development, clubs sports, cultural events"
        canonicalUrl="/student-life"
        ogImage="/images/og-image.jpg"
      />
      
      <div className="pt-20 min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-purple-900 to-blue-800 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold font-display mb-4">Student Life</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Beyond Academics - Holistic Development Through Activities
          </p>
        </div>
      </section>

      {/* Activities Grid */}
      <section id="activities" className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold font-display text-blue-900 text-center mb-12">
            Co-Curricular Activities
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {activities.map((activity, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all"
              >
                <div className={`${activity.color} p-6`}>
                  <activity.icon className="text-5xl text-white mx-auto" />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-blue-900 mb-3">{activity.title}</h3>
                  <p className="text-gray-600">{activity.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sports Section */}
      <section id="sports" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold font-display text-blue-900 text-center mb-12">
            Sports Excellence
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-8 shadow-lg"
            >
              <h3 className="text-2xl font-bold text-blue-900 mb-4">Indoor Sports</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Table Tennis</li>
                <li>• Chess</li>
                <li>• Carrom</li>
                <li>• Badminton Court</li>
                <li>• Indoor Gymnasium</li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 shadow-lg"
            >
              <h3 className="text-2xl font-bold text-blue-900 mb-4">Outdoor Sports</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Basketball Court</li>
                <li>• Football Ground</li>
                <li>• Cricket Pitch</li>
                <li>• Volleyball Court</li>
                <li>• Athletic Track</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section id="achievements" className="py-16 bg-gradient-to-br from-blue-50 to-purple-100">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold font-display text-blue-900 text-center mb-12">
            Our Achievements
          </h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { number: '50+', label: 'Medals Won', sublabel: 'In various competitions' },
                { number: '25+', label: 'Trophies', sublabel: 'Inter-school events' },
                { number: '100+', label: 'Certificates', sublabel: 'Student achievements' }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-xl p-8 text-center"
                >
                  <div className="text-5xl font-bold text-blue-900 mb-2">{stat.number}</div>
                  <div className="text-xl font-semibold text-gray-800 mb-1">{stat.label}</div>
                  <div className="text-sm text-gray-600">{stat.sublabel}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Annual Events */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold font-display text-blue-900 text-center mb-12">
            Annual Events
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: 'Annual Sports Day', desc: 'Grand athletic meet', month: 'February' },
              { name: 'Cultural Fest', desc: 'Talent showcase', month: 'August' },
              { name: 'Science Exhibition', desc: 'Innovation display', month: 'November' },
              { name: 'Annual Function', desc: 'Celebration evening', month: 'March' }
            ].map((event, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-blue-500 to-purple-600 text-white rounded-2xl p-6 text-center shadow-lg"
              >
                <h3 className="text-xl font-bold mb-2">{event.name}</h3>
                <p className="text-blue-100 mb-3">{event.desc}</p>
                <span className="inline-block bg-white/20 px-4 py-1 rounded-full text-sm">
                  {event.month}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default StudentLifePage;
